// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useStore from "@/hooks/useStore";

// --- Logic --------------------------------------------------------------------------------------

export default function usePermission() {
    const client = useAuth((state) => state.client);
    const storeId = useStore((state) => state.storeId);

    const canView = (model: string): boolean => {
        if (!client || !storeId) return false;
        if (client.owner.includes(storeId)) return true;
        const access = client.storeAccess.find((a) => a.store === storeId);
        if (!access) return false;
        return access.permissions.some((p) => p.model === model);
    };

    const canEdit = (model: string): boolean => {
        if (!client || !storeId) return false;
        if (client.owner.includes(storeId)) return true;
        const access = client.storeAccess.find((a) => a.store === storeId);
        if (!access) return false;
        const perm = access.permissions.find((p) => p.model === model);
        return perm?.actions.includes("edit") ?? false;
    };

    // Like canView("branches") but also checks branch-level scope
    const canViewBranch = (branchId: string): boolean => {
        if (!client || !storeId) return false;
        if (client.owner.includes(storeId)) return true;
        const access = client.storeAccess.find((a) => a.store === storeId);
        if (!access) return false;
        if (!access.permissions.some((p) => p.model === "branches")) return false;
        if (access.branches === "all") return true;
        return access.branches.includes(branchId);
    };

    // Like canEdit("branches") but also checks branch-level scope
    const canEditBranch = (branchId: string): boolean => {
        if (!client || !storeId) return false;
        if (client.owner.includes(storeId)) return true;
        const access = client.storeAccess.find((a) => a.store === storeId);
        if (!access) return false;
        const perm = access.permissions.find((p) => p.model === "branches");
        if (!perm?.actions.includes("edit")) return false;
        if (access.branches === "all") return true;
        return access.branches.includes(branchId);
    };

    return { canView, canEdit, canViewBranch, canEditBranch };
}
